import { unsavedChangesGuard } from '@shared/services/page-save-bar.service';
import { Routes } from '@angular/router';
import { authRoutes } from './core/auth/auth.routes';
import { authGuard } from './core/auth/guards/auth.guard';
import { organizationRoutes } from './features/organizations/organization.routes';
import { organizationGuard } from './features/organizations/guards/organization-guard';
import { Layout } from './shared/components/layout/layout';
import { DocsLayout } from './shared/components/docs-layout/docs-layout';
import { Dashboard } from './features/dashboard/pages/dashboard/dashboard';
import { CampaignList } from './features/campaigns/pages/campaign-list/campaign-list';
import { CampaignDetail } from './features/campaigns/pages/campaign-detail/campaign-detail';
import { AddressesPage } from './features/database/pages/addresses-page/addresses-page';
import { AnalysesPage } from './features/database/pages/analyses-page/analyses-page';
import { analysesRoutes } from './features/analyses/analyses.routes';
import { catalogRoutes } from './features/catalog/catalog.routes';

export const routes: Routes = [
  // ============================================================================
  // Public Documentation Route (accessible without login)
  // ============================================================================
  {
    path: 'docs',
    component: DocsLayout,
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/help/pages/docs-index/docs-index').then(
            (m) => m.DocsIndex,
          ),
        data: { title: 'Dokumentation' },
      },
      {
        path: ':slug',
        loadComponent: () =>
          import('./features/help/pages/docs-page/docs-page').then(
            (m) => m.DocsPage,
          ),
        data: { title: 'Dokumentation' },
      },
    ],
  },

  // ============================================================================
  // Public Verification / Analysis View Route (outside layout)
  // ============================================================================
  {
    path: 'public/analyses/:id',
    loadComponent: () =>
      import('./features/public/pages/public-analysis/public-analysis').then(
        (m) => m.PublicAnalysis,
      ),
    data: { title: 'Analysedetails' },
  },

  // ============================================================================
  // Authentication Routes (outside layout)
  // ============================================================================
  ...authRoutes,

  // ============================================================================
  // Organization Management Routes (outside layout)
  // ============================================================================
  ...organizationRoutes,

  // ============================================================================
  // Invitation Acceptance Route (outside layout)
  // ============================================================================
  {
    path: 'invitations/accept',
    loadComponent: () =>
      import('./features/organizations/pages/accept-invitation/accept-invitation').then(
        (m) => m.AcceptInvitation,
      ),
  },

  // ============================================================================
  // Invite Code Redirect Route (outside layout)
  // ============================================================================
  {
    path: 'invite/:code',
    redirectTo: (route) => {
      const code = route.params['code'];
      return `/invitations/accept?code=${code}`;
    },
  },

  // ============================================================================
  // User Routes (outside organization scope)
  // ============================================================================
  {
    path: 'user',
    component: Layout,
    canActivate: [authGuard],
    children: [
      {
        path: 'settings',
        loadComponent: () =>
          import('./features/user/pages/user-settings/user-settings').then(
            (m) => m.UserSettings,
          ),
        data: { title: 'Kontoeinstellungen' },
      },
    ],
  },

  // ============================================================================
  // Main Application Layout with Protected Routes
  // ============================================================================
  {
    path: '',
    component: Layout,
    canActivate: [authGuard],
    children: [
      // ========================================================================
      // Organization-Scoped Routes (preferred approach)
      // ========================================================================
      {
        path: 'organizations/:slug',
        canActivate: [organizationGuard],
        children: [
          {
            path: '',
            redirectTo: 'dashboard',
            pathMatch: 'full',
          },
          {
            path: 'dashboard',
            component: Dashboard,
            data: { title: 'Übersicht' },
          },
          {
            path: 'overview',
            component: Dashboard,
            data: { title: 'Übersicht' },
          },
          {
            path: 'campaigns',
            component: CampaignList,
            data: { title: 'Kampagnen' },
          },
          {
            path: 'campaigns/:id',
            component: CampaignDetail,
            canDeactivate: [(component: CampaignDetail) => component.canDeactivate()],
            data: { title: 'Kampagne' },
          },
          {
            path: 'plugin/:id',
            canDeactivate: [unsavedChangesGuard],
            loadComponent: () =>
              import('./features/plugins/pages/plugin-instance-detail/plugin-instance-detail').then(
                (m) => m.PluginInstanceDetail,
              ),
            data: { title: 'Plugin' },
          },
          {
            path: 'plugin',
            loadComponent: () =>
              import('./features/plugins/pages/plugin-instances/plugin-instances').then(
                (m) => m.PluginInstances,
              ),
            data: { title: 'Plugin' },
          },
          {
            path: 'database',
            data: { title: 'Datenbank' },
            children: [
              {
                path: '',
                redirectTo: 'addresses',
                pathMatch: 'full',
              },
              {
                path: 'addresses',
                component: AddressesPage,
                data: { title: 'Adressen' },
              },
              {
                path: 'analyses',
                component: AnalysesPage,
                data: { title: 'Analysen' },
              },
            ],
          },
          {
            path: 'settings',
            canDeactivate: [unsavedChangesGuard],
            loadComponent: () =>
              import('./features/organizations/pages/organization-settings/organization-settings').then(
                (m) => m.OrganizationSettings,
              ),
            data: { title: 'Einstellungen' },
          },
          {
            path: 'catalog',
            data: { title: 'Katalog' },
            children: catalogRoutes,
          },
          {
            path: 'solar-components',
            redirectTo: 'catalog',
          },
          {
            path: 'analyses',
            children: analysesRoutes,
          },
        ],
      },

      // ========================================================================
      // Catch-All: Guard will redirect to organization-scoped route
      // This handles the default empty route and legacy routes
      // ========================================================================
      {
        path: '',
        canActivate: [organizationGuard],
        component: Dashboard, // Placeholder, guard will navigate
        data: { title: 'Übersicht' },
      },
      {
        path: ':path',
        canActivate: [organizationGuard],
        component: Dashboard, // Placeholder, guard will navigate
        data: { title: 'Übersicht' },
      },
    ],
  },

  // ============================================================================
  // Wildcard Route (404 - must be last)
  // ============================================================================
  {
    path: '**',
    redirectTo: '',
  },
];
